/**
 * useAdvisor.ts — Task E1
 *
 * React hook that wires the advisor runtime to the live app:
 *  - Auth0 token → createAdvisorClient (no API key in browser)
 *  - ApiClient   → buildTools context (tenant-isolated data reads)
 *  - URL state   → serializeView snapshot injected each turn
 *  - Navigation  → applyNav / applyUndo exposed to the panel
 *
 * Streaming fold strategy:
 *  - text events      → append text to the current assistant UiMessage
 *  - tool_result (ui) → append ToolUi chip to the current assistant UiMessage
 *  - error events     → push a SEPARATE error UiMessage (no silent fallback)
 *  - done             → no-op (finalization is implicit)
 *
 * State immutability: every setMessages call builds a new array with new
 * message objects — no in-place mutation.
 */

import { useState, useRef, useCallback, useMemo, useEffect } from 'react'
import { useSearchParams, useLocation, useNavigate } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import { useApiClient } from '../../api/useApiClient'
import { useSetMultipleSearchParams } from '../../state/useSetMultipleSearchParams'
import { createAdvisorClient } from '../runtime/client'
import { runAdvisorTurn } from '../runtime/loop'
import type { AdvisorEvent, AdvisorMessage, ToolUi } from '../runtime/types'
import { assembleSystem, viewContextMessage } from '../grounding/assembler'
import { serializeView } from '../grounding/currentView'
import { createInMemoryKnowledge } from '../grounding/inMemoryKnowledge'
import { ALL_UNITS } from '../grounding/knowledge'
import { buildTools } from '../tools/buildTools'
import { routeFamilyOf } from '../../state/routeStateContract'
import type { OverviewHandle } from './useDatasetOverview'

// ---------------------------------------------------------------------------
// Module-level singleton — created once per JS context, never re-created.
// ---------------------------------------------------------------------------

const kb = createInMemoryKnowledge(ALL_UNITS)

/** Friendly labels for the pending-strip activity line. */
const TOOL_ACTIVITY: Record<string, string> = {
  query_kpi: 'Fetching live data…',
  search_knowledge: 'Consulting knowledge…',
  describe_page: 'Reading page details…',
  propose_navigation: 'Preparing a navigation proposal…',
}

export function activityForTool(name: string): string {
  return (
    TOOL_ACTIVITY[name] ?? (name.startsWith('set_') ? 'Adjusting the view…' : `Running ${name}…`)
  )
}

// ---------------------------------------------------------------------------
// sessionStorage persistence — conversation survives reloads within the tab.
// ---------------------------------------------------------------------------

export const STORAGE_KEY = 'advisor.conversation.v1'

interface StoredConversation {
  messages: UiMessage[]
  history: AdvisorMessage[]
}

/**
 * Read the stored conversation. A corrupt or wrong-shaped cache is dropped
 * with a console.warn — an ephemeral display cache is the one place the
 * no-silent-fallback rule relaxes to "loud warning + clean slate" (throwing
 * here would brick the advisor over stale storage).
 */
export function loadConversation(storage: Storage = sessionStorage): StoredConversation | null {
  const raw = storage.getItem(STORAGE_KEY)
  if (raw === null) return null
  try {
    const parsed = JSON.parse(raw) as { messages?: unknown; history?: unknown }
    if (!Array.isArray(parsed.messages) || !Array.isArray(parsed.history)) {
      throw new Error('unexpected conversation cache shape')
    }
    return parsed as unknown as StoredConversation
  } catch (err) {
    console.warn('advisor: dropping unreadable conversation cache', err)
    storage.removeItem(STORAGE_KEY)
    return null
  }
}

// ---------------------------------------------------------------------------
// History trimming (R4) ─────────────────────────────────────────────────────
// Caps what is SENT per turn. Cuts only at exchange starts — a user message
// with string content — so a tool_use/tool_result pair is never split (the
// API rejects tool_result without its tool_use). The UI transcript in `messages`
// stays full; historyRef is reassigned from the kept suffix after each turn,
// so the dropped prefix is gone from model-facing history (behavior-neutral:
// re-trimming the kept suffix + new turn yields the same sent window).
// ---------------------------------------------------------------------------

export const TRIM_BUDGET_CHARS = 32_000

export function trimHistory(
  history: AdvisorMessage[],
  budgetChars: number = TRIM_BUDGET_CHARS,
): { kept: AdvisorMessage[]; dropped: number } {
  const size = (m: AdvisorMessage): number => JSON.stringify(m.content).length
  let total = history.reduce((s, m) => s + size(m), 0)
  if (total <= budgetChars) return { kept: history, dropped: 0 }

  let start = 0
  while (total > budgetChars) {
    let next = start + 1
    while (
      next < history.length &&
      !(history[next]!.role === 'user' && typeof history[next]!.content === 'string')
    ) {
      next++
    }
    if (next >= history.length) break // cannot cut further without emptying — keep the tail
    for (let i = start; i < next; i++) total -= size(history[i]!)
    start = next
  }
  return { kept: history.slice(start), dropped: start }
}

// ---------------------------------------------------------------------------
// UiMessage — the shape the advisor panel renders
// ---------------------------------------------------------------------------

export interface UiMessage {
  role: 'user' | 'assistant'
  text: string
  /** Tool side-effects emitted during the turn (nav proposals, steer chips…) */
  chips?: ToolUi[]
  /** When true, this message is an explicit error notification. */
  error?: boolean
  /** When true, render as a subtle centered notice (e.g. "Stopped."), not a bubble. */
  notice?: boolean
}

// ---------------------------------------------------------------------------
// useAdvisor
// ---------------------------------------------------------------------------

export function useAdvisor(overviewHandle?: OverviewHandle): {
  messages: UiMessage[]
  pending: boolean
  activity: string | null
  send: (text: string) => Promise<void>
  applyNav: (ui: Extract<ToolUi, { kind: 'nav' }>) => void
  applyUndo: (ui: Extract<ToolUi, { kind: 'steer' }>) => void
  stop: () => void
} {
  // ── External hooks ────────────────────────────────────────────────────────
  const apiClient = useApiClient()
  const { getAccessTokenSilently } = useAuth0()
  const setParams = useSetMultipleSearchParams()
  const [searchParams] = useSearchParams()
  const location = useLocation()
  const navigate = useNavigate()

  // ── URL helpers (stable until URL changes) ────────────────────────────────
  const getParams = useCallback(() => searchParams, [searchParams])
  const getView = useCallback(
    () => serializeView(location.pathname, searchParams),
    [location.pathname, searchParams],
  )
  // FU-49: live route family for the steer tools' route gate — read at run() time
  // via this callback so it always reflects the CURRENT location, not the location
  // at panel mount.
  const getRouteFamily = useCallback(() => routeFamilyOf(location.pathname), [location.pathname])

  // Latest overview in a ref: send() and the tools read it lazily without
  // rebuilding the send callback when the (once-per-session) build resolves.
  const overviewRef = useRef<OverviewHandle>({ overview: null, failure: null })
  overviewRef.current = overviewHandle ?? { overview: null, failure: null }

  const getOverview = useCallback(() => overviewRef.current.overview, [])

  // ── Advisor context passed to buildTools ──────────────────────────────────
  const ctx = useMemo(
    () => ({ apiClient, setParams, getParams, getView, kb, getOverview, getRouteFamily }),
    [apiClient, setParams, getParams, getView, getOverview, getRouteFamily],
  )

  // ── Advisor client (Anthropic SDK → relay, Auth0 bearer injected per-req) ─
  const client = useMemo(
    () => createAdvisorClient(() => getAccessTokenSilently()),
    [getAccessTokenSilently],
  )

  // ── React state ───────────────────────────────────────────────────────────

  // Restore once per mount; lazy initializers keep the parse out of re-renders.
  const restoredRef = useRef<StoredConversation | null | undefined>(undefined)
  if (restoredRef.current === undefined) restoredRef.current = loadConversation()

  const [messages, setMessages] = useState<UiMessage[]>(restoredRef.current?.messages ?? [])
  const [pending, setPending] = useState(false)
  const [activity, setActivity] = useState<string | null>(null)

  /**
   * Conversation history kept in a ref so it survives re-renders without
   * causing unnecessary effect re-runs.  History is NOT part of UI state —
   * it is only read by the next send() call.
   */
  const historyRef = useRef<AdvisorMessage[]>(restoredRef.current?.history ?? [])

  // Persist at turn boundaries only (pending=false): mid-stream writes would fire
  // per text delta for no benefit. historyRef is committed before pending clears
  // (send's finally), so a turn-end snapshot is always internally consistent.
  // Skip the very first (mount) run: it fires whether or not there was anything
  // to restore, and would otherwise immediately re-write an empty conversation
  // over a cache that loadConversation() just dropped-and-removed for being
  // corrupt — turning the "loud warning + clean slate" into a silent respawn.
  const mountedRef = useRef(false)
  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true
      return
    }
    if (pending) return
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ messages, history: historyRef.current }))
    } catch {
      // Quota exceeded / storage unavailable — display state still works; next
      // turn retries. Not silent-fallback territory: nothing behaves differently.
    }
  }, [messages, pending])

  /**
   * Abort plumbing for the user-initiated Stop button.
   *  - abortRef holds the controller for the in-flight turn (null when idle).
   *  - userAbortedRef distinguishes a user-initiated abort from any other
   *    error/throw, so the fold routes it to a calm notice instead of an
   *    error bubble.
   */
  const abortRef = useRef<AbortController | null>(null)
  const userAbortedRef = useRef(false)

  /** R4: the "(older messages trimmed)" notice renders once per session. */
  const trimNoticeShownRef = useRef(false)

  // ── send ──────────────────────────────────────────────────────────────────

  const send = useCallback(
    async (text: string) => {
      if (!text.trim()) return

      // Push the user's message immediately (before async work)
      setMessages((prev) => [...prev, { role: 'user' as const, text }])
      setPending(true)
      setActivity('Thinking…')

      const controller = new AbortController()
      abortRef.current = controller
      userAbortedRef.current = false

      try {
        const ov = overviewRef.current
        const system = assembleSystem(
          kb,
          ov.overview ?? (ov.failure !== null ? { unavailable: ov.failure } : null),
        )
        const tools = buildTools(ctx)
        const view = ctx.getView()

        // R4: cap what is sent; the notice renders once per session, loudly.
        const { kept, dropped } = trimHistory(historyRef.current)
        if (dropped > 0 && !trimNoticeShownRef.current) {
          trimNoticeShownRef.current = true
          setMessages((prev) => [
            ...prev,
            { role: 'assistant' as const, text: '(older messages trimmed)', notice: true as const },
          ])
        }

        const turnMessages: AdvisorMessage[] = [
          ...kept,
          { role: 'user' as const, content: viewContextMessage(view, kb) },
          { role: 'user' as const, content: text },
        ]

        /**
         * Mutable accumulator for the current assistant text.
         * Updated synchronously inside onEvent before setMessages is called,
         * so each functional updater captures the correct snapshot at enqueue time.
         */
        let currentText = ''

        /**
         * Fold each streaming event into UI state.
         *
         * All setMessages calls use functional updaters so they compose
         * correctly even when React batches multiple calls in one flush.
         *
         * Design note (streaming fold correctness):
         *  - `text` events: capture `textSnapshot` at enqueue time → updater
         *    appends or pushes with the snapshot value.
         *  - `tool_result` events: capture `chip` at enqueue time → updater
         *    appends the chip to the existing chips array via prev-state spread.
         *    This is pure and handles multiple chips without double-counting.
         *  - `error` events: push a SEPARATE message with error:true (never
         *    swallowed — explicit per global-constraints.md).
         *  - `thinking`, `tool_start`: update the transient `activity` label
         *    shown in the pending strip (no message-list side-effect).
         *  - `done`: no UI side-effect (finalization is implicit).
         */
        const onEvent = (e: AdvisorEvent): void => {
          switch (e.type) {
            case 'text': {
              setActivity('Writing…')
              currentText += e.text
              // Snapshot at enqueue time — safe even with React batching
              const textSnapshot = currentText
              setMessages((prev) => {
                const last = prev.at(-1)
                if (last?.role === 'assistant' && !last.error && !last.notice) {
                  // Update the in-progress assistant message
                  return [...prev.slice(0, -1), { ...last, text: textSnapshot }]
                }
                // Push a new assistant message (first text event of this turn)
                return [...prev, { role: 'assistant' as const, text: textSnapshot }]
              })
              break
            }

            case 'tool_result': {
              if (e.ui !== undefined) {
                // Capture the chip value at enqueue time
                const chip = e.ui
                setMessages((prev) => {
                  const last = prev.at(-1)
                  if (last?.role === 'assistant' && !last.error && !last.notice) {
                    return [...prev.slice(0, -1), { ...last, chips: [...(last.chips ?? []), chip] }]
                  }
                  // No assistant message yet — push one with just the chip
                  return [...prev, { role: 'assistant' as const, text: currentText, chips: [chip] }]
                })
              }
              break
            }

            case 'error':
              if (userAbortedRef.current) {
                // User-initiated abort — a calm notice, not an error bubble.
                setMessages((prev) => [
                  ...prev,
                  { role: 'assistant' as const, text: 'Stopped.', notice: true as const },
                ])
              } else {
                // Explicit error message — NEVER silently swallowed
                setMessages((prev) => [
                  ...prev,
                  { role: 'assistant' as const, text: e.message, error: true as const },
                ])
              }
              break

            case 'thinking':
              // Model reasoning — not surfaced as text (display:summarized),
              // only as a transient activity label.
              setActivity('Thinking…')
              break

            case 'tool_start':
              setActivity(activityForTool(e.name))
              break

            case 'done':
              // Finalization is implicit (pending/activity cleared in finally)
              break
          }
        }

        const updatedHistory = await runAdvisorTurn({
          client,
          system,
          messages: turnMessages,
          tools,
          onEvent,
          signal: controller.signal,
        })

        historyRef.current = updatedHistory
      } catch (err) {
        if (userAbortedRef.current) {
          // An abort can reject through getAccessTokenSilently/the SDK before
          // the loop's own try/catch sees it — route it the same way.
          setMessages((prev) => [
            ...prev,
            { role: 'assistant' as const, text: 'Stopped.', notice: true as const },
          ])
        } else {
          // Unhandled throw (e.g. network, relay down) — surface explicitly
          const message = err instanceof Error ? err.message : String(err)
          setMessages((prev) => [
            ...prev,
            { role: 'assistant' as const, text: message, error: true as const },
          ])
        }
      } finally {
        setPending(false)
        setActivity(null)
        abortRef.current = null
      }
    },
    [client, ctx],
  )

  // ── stop ──────────────────────────────────────────────────────────────────

  const stop = useCallback(() => {
    userAbortedRef.current = true
    abortRef.current?.abort()
  }, [])

  // ── applyNav ──────────────────────────────────────────────────────────────

  const applyNav = useCallback(
    (ui: Extract<ToolUi, { kind: 'nav' }>) => {
      if (ui.presetView && Object.keys(ui.presetView).length > 0) {
        navigate({
          pathname: ui.route,
          search: '?' + new URLSearchParams(ui.presetView).toString(),
        })
      } else {
        navigate(ui.route)
      }
    },
    [navigate],
  )

  // ── applyUndo ─────────────────────────────────────────────────────────────

  const applyUndo = useCallback(
    (ui: Extract<ToolUi, { kind: 'steer' }>) => {
      setParams(ui.undo)
    },
    [setParams],
  )

  return { messages, pending, activity, send, applyNav, applyUndo, stop }
}
